import FontAwesome5 from '@expo/vector-icons/FontAwesome5';
import {
  responsiveFontSize,
  useResponsiveFontSize,
  useResponsiveHeight,
} from '@/components/Util/reactResponsiveDimensions';
import { useEffect } from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import { View as AnimatableView } from 'react-native-animatable';
import { useExerciseRecord } from '@/hooks/useExerciseRecord';
import { PressableNeolexon } from '@/components/PressableNeolexon';

interface SpeakingHeaderProps {
  uniqueKey: string;
  currentExercise: number;
  timeoutSeconds: number;
  isVideoPlaying?: boolean;
  handleIsRecording: (isRecording: boolean) => void;
  handleIsAudioPlaying?: (isAudioPlaying: boolean) => void;
  selectedPerformance: 'none' | 'good' | 'bad';
  abortPlayingRecording?: boolean;
  isRecordingDisabled?: boolean;
}

// Taken from github to simulate the column gap functionality in web css
const gap = 8;
export function SpeakingHeader({
  uniqueKey,
  currentExercise,
  timeoutSeconds,
  isVideoPlaying,
  handleIsRecording,
  handleIsAudioPlaying,
  selectedPerformance,
  abortPlayingRecording,
  isRecordingDisabled,
}: SpeakingHeaderProps) {
  const iconSize = useResponsiveFontSize(2.6);
  const marginVertical = useResponsiveHeight(1.1);

  const {
    isRecording,
    startRecording,
    stopRecording,
    playRecording,
    stopPlayingRecording,
    isPlaying: isRecordingPlaying,
    uri,
  } = useExerciseRecord(currentExercise, timeoutSeconds, uniqueKey);

  const styles = StyleSheet.create({
    container: {
      flexDirection: 'row',
      justifyContent: 'center',
      marginVertical,
      paddingHorizontal: gap / -2,
    },
    btn: {
      marginHorizontal: gap / 2,
      width: responsiveFontSize(6.2),
      height: responsiveFontSize(6.2),
      justifyContent: 'center',
      alignItems: 'center',
      borderRadius: Platform.OS === 'web' ? '50%' : 50,
      borderWidth: 1,
      borderColor: '#d3d3d3',
      backgroundColor: '#ffff',
    },
    disabledStyling: {
      opacity: 0.3,
    },
  });

  const handleStartRecording = () => {
    handleIsRecording(true);
    startRecording();
  };
  const handleStopRecording = () => {
    handleIsRecording(false);
    stopRecording(true);
  };

  const RecordingComponent = isRecording ? (
    <AnimatableView
      animation="fadeIn"
      easing="ease-out"
      iterationCount="infinite"
      direction="alternate"
      duration={800}
    >
      <PressableNeolexon
        onPress={handleStopRecording}
        disabled={
          isRecordingPlaying || isVideoPlaying || selectedPerformance !== 'none'
        }
        style={[
          styles.btn,
          (isRecordingPlaying ||
            isVideoPlaying ||
            selectedPerformance !== 'none') &&
            styles.disabledStyling,
        ]}
      >
        <FontAwesome5 name="microphone" size={iconSize} color="red" />
      </PressableNeolexon>
    </AnimatableView>
  ) : (
    <PressableNeolexon
      onPress={handleStartRecording}
      disabled={
        isRecordingPlaying ||
        isVideoPlaying ||
        selectedPerformance !== 'none' ||
        isRecordingDisabled
      }
      style={[
        styles.btn,
        (isRecordingPlaying ||
          isVideoPlaying ||
          selectedPerformance !== 'none' ||
          isRecordingDisabled) &&
          styles.disabledStyling,
      ]}
    >
      <FontAwesome5 name="microphone" size={iconSize} color="red" />
    </PressableNeolexon>
  );

  useEffect(() => {
    if (isRecordingPlaying) {
      handleIsAudioPlaying && handleIsAudioPlaying(true);
    } else {
      handleIsAudioPlaying && handleIsAudioPlaying(false);
    }
    handleIsRecording && handleIsRecording(false);
  }, [isRecordingPlaying]);

  useEffect(() => {
    if (isRecordingPlaying && abortPlayingRecording) {
      stopPlayingRecording();
    }
  }, [isRecordingPlaying, abortPlayingRecording]);

  return (
    <View style={styles.container}>
      {RecordingComponent}
      <PressableNeolexon
        onPress={isRecordingPlaying ? stopPlayingRecording : playRecording}
        disabled={
          isRecording ||
          !uri ||
          isVideoPlaying ||
          selectedPerformance !== 'none'
        }
        style={[
          styles.btn,
          (isRecording ||
            !uri ||
            isVideoPlaying ||
            selectedPerformance !== 'none') &&
            styles.disabledStyling,
        ]}
      >
        <FontAwesome5
          name={isRecordingPlaying ? 'pause' : 'play'}
          size={iconSize}
          color="#3a3a3a"
        />
      </PressableNeolexon>
    </View>
  );
}
